import type {
  ChartThemePayload,
  DashboardThemeTokens,
  ThemeAppearance,
  ThemePresentation,
} from './types';

export const DEFAULT_THEME_PRESENTATION: ThemePresentation = {
  readability: 'large',
  titleStyle: 'accent',
  logoPlacement: 'header',
  footerText: '',
  showDate: true,
};

/** Legacy themes reuse their default logo in both appearances. */
export const resolveThemeLogo = (theme: ChartThemePayload, appearance: ThemeAppearance): string | undefined =>
  appearance === 'dark' ? theme.brand?.darkLogoDataUrl ?? theme.brand?.logoDataUrl : theme.brand?.logoDataUrl;

/** Missing fields inherit at read time. Existing chart tokens remain byte-for-byte intact. */
export const resolveDashboardTokens = (
  theme: ChartThemePayload,
  appearance: ThemeAppearance,
  presenting = false
): DashboardThemeTokens => {
  const mode = theme[appearance];
  const inherited: DashboardThemeTokens = {
    pageBackground: mode.surface.chartBackground,
    cardBackground: mode.surface.chartBackground,
    text: mode.surface.text,
    mutedText: mode.surface.mutedText,
    border: mode.surface.border,
    accent: mode.palette.comparison.current,
    cardRadius: 12,
    cardStyle: 'outlined',
    density: 'comfortable',
    kpiStyle: 'quiet',
  };
  const resolved = { ...inherited, ...mode.dashboard, ...(presenting ? mode.presentation : {}) };
  return {
    ...resolved,
    kpiBackground: resolved.kpiBackground ?? resolved.cardBackground,
    headerBackground: resolved.headerBackground ?? resolved.cardBackground,
    headerText: resolved.headerText ?? resolved.text,
  };
};

export const resolvePresentation = (theme: ChartThemePayload): ThemePresentation => ({
  ...DEFAULT_THEME_PRESENTATION,
  ...theme.presentation,
});

/** A member + raw category key, rather than query order, determines category identity. */
export const categoryColorIndex = (member: string, value: string, count: number): number => {
  let hash = 2166136261;
  for (const char of `${member.split('.').pop()}\0${value}`)
    hash = Math.imul(hash ^ char.charCodeAt(0), 16777619);
  return (hash >>> 0) % Math.max(1, count);
};

export const resolveCategoryColor = (
  theme: ChartThemePayload,
  appearance: ThemeAppearance,
  member: string,
  value: string
): string => {
  const explicit = theme.categoryColors?.find(
    (item) => item.member === member && item.value === value
  );
  const palette = theme[appearance].palette.categorical;
  return explicit?.[appearance] ?? palette[categoryColorIndex(member, value, palette.length)]!;
};

export const presentationChartTheme = (theme: ChartThemePayload): ChartThemePayload => {
  if (resolvePresentation(theme).readability === 'standard') return theme;
  const mode = (appearance: ThemeAppearance) => ({
    ...theme[appearance],
    typography: { ...theme[appearance].typography, scale: 'large' as const },
    components: { ...theme[appearance].components, gridDensity: 'subtle' as const },
    series: { ...theme[appearance].series, fillStyle: 'solid' as const },
    interaction: { ...theme[appearance].interaction, motion: 'reduced' as const },
  });
  return {
    ...theme,
    light: mode('light'),
    dark: mode('dark'),
    chartOverrides: Object.fromEntries(
      Object.entries(theme.chartOverrides).map(([key, value]) => [
        key,
        { ...value, fillStyle: 'solid' },
      ])
    ),
  };
};

/** Embedded charts use their host surface while dedicated chart previews keep chart-specific styling. */
export const dashboardChartTheme = (
  theme: ChartThemePayload,
  presenting = false
): ChartThemePayload => {
  const mode = (appearance: ThemeAppearance) => {
    const tokens = resolveDashboardTokens(theme, appearance, presenting);
    const original = theme[appearance];
    return {
      ...original,
      surface: {
        ...original.surface,
        chartBackground: tokens.cardBackground,
        plotBackground:
          original.surface.plotBackground === original.surface.chartBackground
            ? tokens.cardBackground
            : original.surface.plotBackground,
        text: tokens.text,
        mutedText: tokens.mutedText,
        border: tokens.border,
      },
    };
  };
  const embedded = { ...theme, light: mode('light'), dark: mode('dark') };
  return presenting ? presentationChartTheme(embedded) : embedded;
};
